import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/system";
import { useEffect, useState } from "react";
import { CloseOutlined } from "@mui/icons-material";
import api from "../../../../services/api";
import useStyles from "./styles";

const MS_DIA = 24 * 60 * 60 * 1000;

const ROTULOS_STATUS = {
  ATIVA: "ativa",
  EXPIRANDO: "expirando",
  EXPIRADA: "expirada",
  REVOGADA: "revogada",
  NAO_PROVISIONADA: "aguardando provisionamento",
  NUNCA_EMITIDA: "sem emissão",
  SEM_LICENCA: "sem licença",
};

const diasRestantes = (licenca) => {
  if (!licenca?.expiraEm) return null;

  const restanteMs = new Date(licenca.expiraEm).getTime() - Date.now();
  return restanteMs <= 0 ? 0 : Math.ceil(restanteMs / MS_DIA);
};

const normalizarUrl = (valor) => String(valor || "").trim().replace(/\/+$/, "");

export default function ModalLicenca({
  open,
  setOpen = () => {},
  licenca,
  clientes = [],
  licencas = [],
  setSnackbar = () => {},
  onSalvo = () => {},
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [clienteId, setClienteId] = useState("");
  const [urlBase, setUrlBase] = useState("");
  const [clienteInvalido, setClienteInvalido] = useState(false);
  const [testando, setTestando] = useState(false);
  const [loading, setLoading] = useState(false);

  const edicao = !!licenca;
  const status = licenca?.statusDerivado;
  const dias = diasRestantes(licenca);

  useEffect(() => {
    if (!open) return;
    setClienteId(licenca?.clienteId || "");
    setUrlBase(licenca?.urlBase || "");
    setClienteInvalido(false);
  }, [open, licenca]);

  const clientesDisponiveis = clientes.filter(
    (cliente) =>
      !cliente?.isGrupoEconomico &&
      !licencas.some((item) => item.clienteId === cliente.id),
  );

  const nomeCliente =
    licenca?.cliente?.nomeFantasia ||
    licenca?.cliente?.razaoSocial ||
    licenca?.clienteId;

  const handleCancelar = () => {
    setOpen(false);
    setClienteInvalido(false);
  };

  // reusa a rota de renovação forçada: ela é exatamente a chamada que precisa
  // funcionar, então serve de teste sem endpoint novo
  async function testarUrl() {
    if (normalizarUrl(urlBase) !== normalizarUrl(licenca?.urlBase)) {
      setSnackbar({
        children: "Salve a URL antes de testar.",
        severity: "warning",
      });
      return;
    }

    setTestando(true);
    try {
      const response = await api.post("/interno/licencas/forcar-renovacao", {
        clienteId,
      });

      setSnackbar({
        severity: response?.data?.alcancado ? "success" : "warning",
        children: response?.data?.alcancado
          ? "Instalação alcançada."
          : response?.data?.motivo || "Instalação não foi alcançada.",
      });
    } catch (error) {
      setSnackbar({
        children: error?.response?.data?.error || "Erro ao testar a instalação",
        severity: "error",
      });
    } finally {
      setTestando(false);
    }
  }

  async function handleSalvar() {
    if (!edicao && !clienteId) {
      setClienteInvalido(true);
      setSnackbar({ children: "Selecione um cliente", severity: "error" });
      return;
    }

    setLoading(true);
    try {
      if (edicao) {
        await api.put("/interno/licencas", { clienteId, urlBase });
      } else {
        await api.post("/interno/licencas", { clienteId, urlBase });
      }

      await onSalvo();
      setSnackbar({
        children: edicao ? "Licença atualizada" : "Licença criada",
        severity: "success",
      });
      handleCancelar();
    } catch (error) {
      setSnackbar({
        children: error?.response?.data?.error || "Erro ao salvar licença",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open={open} onClose={handleCancelar} sx={styles.modal}>
      <Paper sx={styles.container}>
        <Box sx={styles.cabecalho}>
          <Box sx={styles.cabecalhoTextos}>
            <Typography sx={styles.sobretitulo}>
              {edicao ? "Editar licença" : "Nova licença"}
            </Typography>
            <Typography sx={styles.titulo}>
              {edicao ? nomeCliente : "Cadastrar instalação"}
            </Typography>
            {edicao && (
              <Box sx={styles.resumo}>
                <Box sx={styles.pontoStatus(status)} />
                <Typography sx={styles.resumoTexto}>
                  {ROTULOS_STATUS[status] || status}
                </Typography>
                {dias !== null && status !== "REVOGADA" && (
                  <>
                    <Typography sx={styles.resumoSeparador}>·</Typography>
                    <Typography sx={styles.resumoTexto}>
                      {dias} {dias === 1 ? "dia" : "dias"}
                    </Typography>
                  </>
                )}
                <Typography sx={styles.resumoId}>{licenca?.clienteId}</Typography>
              </Box>
            )}
          </Box>
          <IconButton onClick={handleCancelar} sx={styles.fechar} size="small">
            <CloseOutlined fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        <Box sx={styles.corpo}>
          <Box sx={styles.secao}>
            <Typography sx={styles.secaoTitulo}>Identificação</Typography>
            {edicao ? (
              <TextField
                label="Cliente"
                value={nomeCliente}
                disabled
                size="small"
                fullWidth
              />
            ) : (
              <FormControl
                size="small"
                fullWidth
                error={clienteInvalido && !clienteId}
              >
                <InputLabel>Cliente</InputLabel>
                <Select
                  label="Cliente"
                  value={clienteId}
                  onChange={(event) => setClienteId(event.target.value)}
                >
                  {clientesDisponiveis.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nomeFantasia || cliente.razaoSocial || cliente.cnpj}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <Box sx={styles.secao}>
            <Typography sx={styles.secaoTitulo}>Instalação</Typography>
            <TextField
              label="URL da instalação"
              placeholder="https://api.cliente.com.br"
              value={urlBase}
              onChange={(event) => setUrlBase(event.target.value)}
              size="small"
              fullWidth
              helperText="Opcional. Usada para notificar a instalação na hora."
              InputProps={{
                endAdornment: edicao ? (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={testarUrl}
                      disabled={testando || !urlBase}
                      sx={styles.botaoTestar}
                    >
                      Testar
                    </Button>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        </Box>

        <Divider />

        <Box sx={styles.rodape}>
          <Typography sx={styles.rodapeAviso}>
            O segredo da instalação é entregue pelo provisionamento, nunca por aqui.
          </Typography>
          <Box sx={styles.rodapeBotoes}>
            <Button sx={styles.botaoCancelar} onClick={handleCancelar}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSalvar}
              disabled={loading}
              sx={styles.botaoSalvar}
            >
              {edicao ? "Salvar" : "Criar"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
}
